import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useUserProfile } from '../hooks/useUserProfile';
import { User, Mail, Lock, Check, X, Loader2, Pencil, AlertCircle, Twitter } from 'lucide-react';

interface ProfileDetailsSectionProps {
  initialFirstName?: string;
  initialLastName?: string;
  userEmail?: string;
  onProfileUpdate?: () => void;
}

export const ProfileDetailsSection: React.FC<ProfileDetailsSectionProps> = ({
  initialFirstName = '',
  initialLastName = '',
  userEmail = '',
  onProfileUpdate
}) => {
  const { user } = useAuth();
  const { profile, updateTwitterHandle, refetch } = useUserProfile();
  const [isEditingTwitter, setIsEditingTwitter] = useState(false);
  const [twitterInput, setTwitterInput] = useState('');
  const [savingTwitter, setSavingTwitter] = useState(false);
  const [twitterError, setTwitterError] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [email, setEmail] = useState(userEmail);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [savingName, setSavingName] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setEmail(userEmail);
  }, [initialFirstName, initialLastName, userEmail]);

  useEffect(() => {
    setTwitterInput(profile?.twitter_handle || '');
  }, [profile?.twitter_handle]);

  const handleSaveTwitter = async () => {
    setTwitterError(null);
    setSavingTwitter(true);
    const result = await updateTwitterHandle(twitterInput);
    setSavingTwitter(false);
    if (result.success) {
      setIsEditingTwitter(false);
      refetch();
      onProfileUpdate?.();
    } else {
      setTwitterError(result.error || 'Failed to update');
    }
  };

  const cancelTwitterEdit = () => {
    setTwitterInput(profile?.twitter_handle || '');
    setTwitterError(null);
    setIsEditingTwitter(false);
  };

  const handleSaveName = async () => {
    if (!user) return;
    
    setNameError(null);
    setSavingName(true);
    
    try {
      const { error } = await supabase
        .from('users')
        .update({ 
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;
      
      setIsEditingName(false);
      onProfileUpdate?.();
    } catch (err) {
      console.error('Error updating name:', err);
      setNameError('Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!user) return;
    
    setEmailError(null);
    setEmailSuccess(null);
    
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (trimmedEmail === userEmail?.toLowerCase()) {
      setIsEditingEmail(false);
      return;
    }
    
    setSavingEmail(true);
    
    try {
      // Update email in auth.users (requires confirmation)
      const { error } = await supabase.auth.updateUser({ email: trimmedEmail });
      
      if (error) throw error;
      
      setEmailSuccess('Confirmation email sent to your new address. Please check your inbox.');
      setIsEditingEmail(false);
    } catch (err: any) {
      console.error('Error updating email:', err);
      setEmailError(err.message || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;
    
    setPasswordError(null);
    setPasswordSuccess(null);
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setSavingPassword(true);
    
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      
      if (error) throw error;
      
      setPasswordSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangingPassword(false);
    } catch (err: any) {
      console.error('Error changing password:', err);
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const cancelNameEdit = () => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setNameError(null);
    setIsEditingName(false);
  };

  const cancelEmailEdit = () => {
    setEmail(userEmail);
    setEmailError(null);
    setIsEditingEmail(false);
  };

  const cancelPasswordChange = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setIsChangingPassword(false);
  };

  return (
    <div className="space-y-4">
      {/* Name Field */}
      <div className="py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Name</span>
          </div>
          {!isEditingName && (
            <button
              onClick={() => setIsEditingName(true)}
              className="text-sm text-theme-primary hover:text-theme-primary/80 font-medium flex items-center gap-1"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          )}
        </div>
        
        {isEditingName ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary"
              />
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary"
              />
            </div>
            {nameError && (
              <p className="text-sm text-red-600 dark:text-red-400">{nameError}</p>
            )}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSaveName}
                disabled={savingName}
                className="px-3 py-1.5 text-sm font-medium text-white bg-theme-primary rounded-lg hover:bg-theme-primary/90 disabled:opacity-50 flex items-center gap-1.5"
              >
                {savingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Save
              </button>
              <button
                onClick={cancelNameEdit}
                disabled={savingName}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-gray-900 dark:text-white">
            {initialFirstName || initialLastName 
              ? `${initialFirstName} ${initialLastName}`.trim() 
              : <span className="text-gray-400 italic">Not set</span>}
          </p>
        )}
      </div>

      {/* Email Field */}
      <div className="py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Mail className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</span>
          </div>
          {!isEditingEmail && (
            <button
              onClick={() => setIsEditingEmail(true)}
              className="text-sm text-theme-primary hover:text-theme-primary/80 font-medium flex items-center gap-1"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          )}
        </div>
        
        {isEditingEmail ? (
          <div className="space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary"
            />
            {emailError && (
              <p className="text-sm text-red-600 dark:text-red-400">{emailError}</p>
            )}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  You'll receive a confirmation email at your new address. Click the link to complete the change.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSaveEmail}
                disabled={savingEmail}
                className="px-3 py-1.5 text-sm font-medium text-white bg-theme-primary rounded-lg hover:bg-theme-primary/90 disabled:opacity-50 flex items-center gap-1.5"
              >
                {savingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Send Confirmation
              </button>
              <button
                onClick={cancelEmailEdit}
                disabled={savingEmail}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-gray-900 dark:text-white">{userEmail}</p>
            {emailSuccess && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">{emailSuccess}</p>
            )}
          </div>
        )}
      </div>

      {/* X / Twitter Handle Field */}
      <div className="py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Twitter className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">X / Twitter</span>
          </div>
          {!isEditingTwitter && (
            <button
              onClick={() => setIsEditingTwitter(true)}
              className="text-sm text-theme-primary hover:text-theme-primary/80 font-medium flex items-center gap-1"
            >
              <Pencil className="h-3.5 w-3.5" />
              {profile?.twitter_handle ? 'Edit' : 'Add'}
            </button>
          )}
        </div>

        {isEditingTwitter ? (
          <div className="space-y-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm font-medium">@</span>
              <input
                type="text"
                value={twitterInput}
                onChange={(e) => setTwitterInput(e.target.value)}
                placeholder="yourhandle"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={20}
                className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Used to @-tag you in shout-outs on X. Letters, numbers and underscores only (max 15).
            </p>
            {twitterError && (
              <p className="text-sm text-red-600 dark:text-red-400">{twitterError}</p>
            )}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSaveTwitter}
                disabled={savingTwitter}
                className="px-3 py-1.5 text-sm font-medium text-white bg-theme-primary rounded-lg hover:bg-theme-primary/90 disabled:opacity-50 flex items-center gap-1.5"
              >
                {savingTwitter ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Save
              </button>
              <button
                onClick={cancelTwitterEdit}
                disabled={savingTwitter}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
            </div>
          </div>
        ) : profile?.twitter_handle ? (
          <a
            href={`https://x.com/${profile.twitter_handle}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-theme-primary hover:underline"
          >
            @{profile.twitter_handle}
          </a>
        ) : (
          <p className="text-gray-400 italic">Not set</p>
        )}
      </div>

      {/* Password Field */}
      <div className="py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Lock className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Password</span>
          </div>
          {!isChangingPassword && (
            <button
              onClick={() => setIsChangingPassword(true)}
              className="text-sm text-theme-primary hover:text-theme-primary/80 font-medium flex items-center gap-1"
            >
              <Pencil className="h-3.5 w-3.5" />
              Change
            </button>
          )}
        </div>
        
        {isChangingPassword ? (
          <div className="space-y-3">
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary"
            />
            {passwordError && (
              <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
            )}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleChangePassword}
                disabled={savingPassword}
                className="px-3 py-1.5 text-sm font-medium text-white bg-theme-primary rounded-lg hover:bg-theme-primary/90 disabled:opacity-50 flex items-center gap-1.5"
              >
                {savingPassword ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Update Password
              </button>
              <button
                onClick={cancelPasswordChange}
                disabled={savingPassword}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-gray-900 dark:text-white">••••••••</p>
            {passwordSuccess && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">{passwordSuccess}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
